/**
 * SeasonalConfigService - Fetches seasonal event configurations from the backend
 *
 * Handles API communication for time-limited events, with caching and fallback
 * to default themes when the server is unavailable.
 */

import type { SeasonalEvent, EventApiResponse } from './types';
import { DEFAULT_EVENTS } from './defaultEvents';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY = 'space_invaders_2030_event_cache';

interface CachedEventData {
  event: SeasonalEvent | null;
  fetchedAt: number;
}

export class SeasonalConfigService {
  private static instance: SeasonalConfigService;
  private baseUrl: string;
  private cachedEvent: SeasonalEvent | null = null;
  private lastFetchTime = 0;
  private isFetching = false;

  private constructor() {
    // Determine API URL based on environment
    const isDev = import.meta.env.DEV;
    this.baseUrl = isDev
      ? 'http://localhost:3002/api'
      : `${window.location.origin}/api`;

    // Load from cache on initialization
    this.loadFromCache();
  }

  static getInstance(): SeasonalConfigService {
    if (!SeasonalConfigService.instance) {
      SeasonalConfigService.instance = new SeasonalConfigService();
    }
    return SeasonalConfigService.instance;
  }

  /**
   * Load cached event data from localStorage
   */
  private loadFromCache(): void {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const data: CachedEventData = JSON.parse(cached);
        if (data.fetchedAt && Date.now() - data.fetchedAt < CACHE_DURATION) {
          this.cachedEvent = data.event;
          this.lastFetchTime = data.fetchedAt;
        }
      }
    } catch (err) {
      console.warn('Failed to load event cache:', err);
    }
  }

  /**
   * Save event data to localStorage cache
   */
  private saveToCache(event: SeasonalEvent | null): void {
    try {
      const data: CachedEventData = {
        event,
        fetchedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.warn('Failed to save event cache:', err);
    }
  }

  /**
   * Fetch the currently active seasonal event from the backend
   */
  async fetchCurrentEvent(): Promise<SeasonalEvent | null> {
    // Return cached if fresh
    if (this.cachedEvent && Date.now() - this.lastFetchTime < CACHE_DURATION) {
      return this.cachedEvent;
    }

    // Prevent concurrent fetches
    if (this.isFetching) {
      return this.cachedEvent;
    }

    this.isFetching = true;

    try {
      const response = await fetch(`${this.baseUrl}/events/current`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If endpoint doesn't exist (404), fall back to default events
        if (response.status === 404) {
          return this.getDefaultActiveEvent();
        }
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result: EventApiResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch event');
      }

      this.cachedEvent = result.data;
      this.lastFetchTime = Date.now();
      this.saveToCache(result.data);

      return result.data;
    } catch (err) {
      console.warn('Failed to fetch event from server, using defaults:', err);
      // Fall back to default events based on current date
      return this.getDefaultActiveEvent();
    } finally {
      this.isFetching = false;
    }
  }

  /**
   * Get a specific event by ID
   */
  async fetchEventById(eventId: string): Promise<SeasonalEvent | null> {
    try {
      const response = await fetch(`${this.baseUrl}/events/${eventId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Check default events
          return DEFAULT_EVENTS.find((e) => e.id === eventId) || null;
        }
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result: EventApiResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch event');
      }

      return result.data;
    } catch (err) {
      console.warn('Failed to fetch event, checking defaults:', err);
      return DEFAULT_EVENTS.find((e) => e.id === eventId) || null;
    }
  }

  /**
   * Get the currently active default event based on the current date
   */
  getDefaultActiveEvent(): SeasonalEvent | null {
    const now = Date.now();
    const activeEvent = DEFAULT_EVENTS.find(
      (event) => event.startDate <= now && event.endDate >= now && event.isActive
    );

    if (activeEvent) {
      this.cachedEvent = activeEvent;
      this.lastFetchTime = now;
      return activeEvent;
    }

    return null;
  }

  /**
   * Get all available default events
   */
  getDefaultEvents(): SeasonalEvent[] {
    return [...DEFAULT_EVENTS];
  }

  /**
   * Check if there's an active event (uses cache if available)
   */
  hasActiveEvent(): boolean {
    if (this.cachedEvent) {
      const now = Date.now();
      return (
        this.cachedEvent.startDate <= now &&
        this.cachedEvent.endDate >= now &&
        this.cachedEvent.isActive
      );
    }
    return false;
  }

  /**
   * Get the cached event without fetching
   */
  getCachedEvent(): SeasonalEvent | null {
    return this.cachedEvent;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cachedEvent = null;
    this.lastFetchTime = 0;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn('Failed to clear event cache:', err);
    }
  }

  /**
   * Force refresh the event data
   */
  async refresh(): Promise<SeasonalEvent | null> {
    this.clearCache();
    return this.fetchCurrentEvent();
  }
}

// Export singleton instance
export const seasonalConfigService = SeasonalConfigService.getInstance();
